import React, { useState, useEffect } from 'react';
import { 
  addFavoriteAsset, 
  addFavoriteVariable, 
  isAssetFavorite, 
  isVariableFavorite,
  getFavoriteAssets,
  getFavoriteVariables
} from '../utils/favorites';

interface AnalysisFormProps {
  onAnalyze: (asset: string, variable: string) => void;
  loading: boolean;
}

const AnalysisForm: React.FC<AnalysisFormProps> = ({ onAnalyze, loading }) => {
  const [asset, setAsset] = useState('');
  const [variable, setVariable] = useState('');
  const [assetFavorites, setAssetFavorites] = useState<string[]>([]);
  const [variableFavorites, setVariableFavorites] = useState<string[]>([]);

  useEffect(() => {
    const loadFavorites = () => {
      setAssetFavorites(getFavoriteAssets());
      setVariableFavorites(getFavoriteVariables());
    };
    loadFavorites();
    
    const handleFavoritesUpdate = () => {
      loadFavorites();
    };
    
    const handleAssetSelected = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      setAsset(customEvent.detail);
    };
    
    const handleVariableSelected = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      setVariable(customEvent.detail);
    };
    
    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);
    window.addEventListener('assetSelected', handleAssetSelected);
    window.addEventListener('variableSelected', handleVariableSelected);
    
    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
      window.removeEventListener('assetSelected', handleAssetSelected);
      window.removeEventListener('variableSelected', handleVariableSelected);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (asset.trim()) {
      onAnalyze(asset.trim(), variable.trim() || '');
    }
  };

  const handleAddAssetFavorite = () => {
    if (asset.trim() && !isAssetFavorite(asset)) {
      addFavoriteAsset(asset);
      setAssetFavorites(getFavoriteAssets());
      window.dispatchEvent(new Event('favoritesUpdated'));
    }
  };

  const handleAddVariableFavorite = () => {
    if (variable.trim() && !isVariableFavorite(variable)) {
      addFavoriteVariable(variable);
      setVariableFavorites(getFavoriteVariables());
      window.dispatchEvent(new Event('favoritesUpdated'));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="asset" className="block text-sm font-medium text-gray-700 mb-2">
            投资标的
          </label>
          <div className="relative">
            <input
              type="text"
              id="asset"
              value={asset}
              onChange={(e) => setAsset(e.target.value)}
              placeholder="例如：黄金、股票、债券等"
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            {asset.trim() && !isAssetFavorite(asset) && (
              <button
                type="button"
                onClick={handleAddAssetFavorite}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 hover:text-blue-700 transition-colors"
                title="收藏此投资标的"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
            )}
            {asset.trim() && isAssetFavorite(asset) && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600" title="已收藏">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="variable" className="block text-sm font-medium text-gray-700 mb-2">
            事件或数据因子
          </label>
          <div className="relative">
            <input
              type="text"
              id="variable"
              value={variable}
              onChange={(e) => setVariable(e.target.value)}
              placeholder="例如：美联储降息、地缘政治紧张等（可选，不填则自动分析）"
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {variable.trim() && !isVariableFavorite(variable) && (
              <button
                type="button"
                onClick={handleAddVariableFavorite}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500 hover:text-green-700 transition-colors"
                title="收藏此变量"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
            )}
            {variable.trim() && isVariableFavorite(variable) && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 text-green-600" title="已收藏">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? '分析中...' : '开始分析'}
        </button>
      </form>

      <div className="mt-4 text-sm text-gray-500">
        <p className="font-medium mb-1">示例：</p>
        <ul className="list-disc list-inside space-y-1">
          <li>投资标的：黄金，事件或数据因子：美联储降息</li>
          <li>投资标的：股票，事件或数据因子：美联储降息</li>
          <li>投资标的：黄金，事件或数据因子：地缘政治紧张</li>
        </ul>
      </div>
    </div>
  );
};

export default AnalysisForm;


